import { supabase } from "@/integrations/supabase/client";
import { startOfDay, startOfWeek, endOfWeek, subWeeks } from "date-fns";

export interface WorkloadMetrics {
  autoApprovedToday: number;
  autoApprovedThisWeek: number;
  hoursSavedToday: number;
  hoursSavedThisWeek: number;
  pendingHighRisk: number;
  pendingMediumRisk: number;
  pendingLowRisk: number;
  totalPending: number;
  avgAutoApprovalTime: number;
  autoApprovalRate: number;
  manualReviewRequired: number;
}

export interface AutoApprovalTrend {
  date: string;
  count: number;
  hoursSaved: number;
}

const MINUTES_SAVED_PER_AUTO_APPROVAL = 14;

/**
 * Get comprehensive workload metrics for compliance staff
 */
export async function getWorkloadMetrics(): Promise<WorkloadMetrics> {
  const today = startOfDay(new Date());
  const weekStart = startOfWeek(new Date());
  const weekEnd = endOfWeek(new Date());

  // Get auto-approved deals today
  const { data: autoApprovedToday } = await supabase
    .from("ai_decision_logs")
    .select("id")
    .eq("decision_type", "auto_approval")
    .eq("output_decision", "approved")
    .gte("created_at", today.toISOString());

  // Get auto-approved deals this week
  const { data: autoApprovedThisWeek } = await supabase
    .from("ai_decision_logs")
    .select("id")
    .eq("decision_type", "auto_approval")
    .eq("output_decision", "approved")
    .gte("created_at", weekStart.toISOString())
    .lte("created_at", weekEnd.toISOString());

  // Get pending deals by risk level
  const { data: pendingDeals } = await supabase
    .from("deals")
    .select("ai_risk_score")
    .eq("status", "pending_compliance");

  // Get total deals for approval rate calculation
  const { data: allDeals } = await supabase
    .from("deals")
    .select("id, auto_approved")
    .gte("created_at", weekStart.toISOString());

  // Calculate metrics
  const autoApprovedTodayCount = autoApprovedToday?.length || 0;
  const autoApprovedThisWeekCount = autoApprovedThisWeek?.length || 0;

  const hoursSavedToday = (autoApprovedTodayCount * MINUTES_SAVED_PER_AUTO_APPROVAL) / 60;
  const hoursSavedThisWeek = (autoApprovedThisWeekCount * MINUTES_SAVED_PER_AUTO_APPROVAL) / 60;

  // Categorize pending deals by risk
  const pendingHighRisk = pendingDeals?.filter(d => (d.ai_risk_score || 0) >= 70).length || 0;
  const pendingMediumRisk = pendingDeals?.filter(d => {
    const score = d.ai_risk_score || 0;
    return score >= 40 && score < 70;
  }).length || 0;
  const pendingLowRisk = pendingDeals?.filter(d => (d.ai_risk_score || 0) < 40).length || 0;
  const totalPending = pendingDeals?.length || 0;

  // Calculate auto-approval rate
  const totalDealsThisWeek = allDeals?.length || 0;
  const autoApprovedCount = allDeals?.filter(d => d.auto_approved).length || 0;
  const autoApprovalRate = totalDealsThisWeek > 0 
    ? (autoApprovedCount / totalDealsThisWeek) * 100 
    : 0;

  const manualReviewRequired = totalPending;

  // Average auto-approval time (simulated - could be calculated from actual timestamps)
  const avgAutoApprovalTime = 0.5; // 30 seconds average

  return {
    autoApprovedToday: autoApprovedTodayCount,
    autoApprovedThisWeek: autoApprovedThisWeekCount,
    hoursSavedToday,
    hoursSavedThisWeek,
    pendingHighRisk,
    pendingMediumRisk,
    pendingLowRisk,
    totalPending,
    avgAutoApprovalTime,
    autoApprovalRate,
    manualReviewRequired,
  };
}

/**
 * Get daily auto-approval trends for the past week
 */
export async function getAutoApprovalTrends(): Promise<AutoApprovalTrend[]> {
  const weekStart = startOfWeek(new Date());
  const prevWeekStart = subWeeks(weekStart, 1);

  const { data: decisions } = await supabase
    .from("ai_decision_logs")
    .select("created_at")
    .eq("decision_type", "auto_approval")
    .eq("output_decision", "approved")
    .gte("created_at", prevWeekStart.toISOString())
    .order("created_at", { ascending: true });

  if (!decisions) return [];

  // Group by date
  const trendMap = new Map<string, number>();
  
  decisions.forEach(decision => {
    const date = new Date(decision.created_at).toISOString().split('T')[0];
    trendMap.set(date, (trendMap.get(date) || 0) + 1);
  });

  // Convert to array and calculate hours saved
  return Array.from(trendMap.entries()).map(([date, count]) => ({
    date,
    count,
    hoursSaved: (count * MINUTES_SAVED_PER_AUTO_APPROVAL) / 60,
  }));
}
